// NotificationCenter Component
// Displays upcoming opportunities, cautions, yogas, and predictions

import { useState, useEffect, useRef } from 'react';
import { keyframes } from '@emotion/react';
import { Box, Button, IconButton, Badge, Card, Typography, Divider, Chip } from '@mui/joy';
import NotificationsIcon from '@mui/icons-material/Notifications';
import NotificationsActiveIcon from '@mui/icons-material/NotificationsActive';
import TrendingUpIcon from '@mui/icons-material/TrendingUp';
import WarningIcon from '@mui/icons-material/Warning';
import AutoAwesomeIcon from '@mui/icons-material/AutoAwesome';
import CalendarTodayIcon from '@mui/icons-material/CalendarToday';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import CloseIcon from '@mui/icons-material/Close';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline';
import SwapHorizIcon from '@mui/icons-material/SwapHoriz';
import TodayIcon from '@mui/icons-material/Today';
import DateRangeIcon from '@mui/icons-material/DateRange';
import CalendarMonthIcon from '@mui/icons-material/CalendarMonth';
import WbSunnyIcon from '@mui/icons-material/WbSunny';
import WorkspacePremiumIcon from '@mui/icons-material/WorkspacePremium';

// Bell ring: rotate pivot from top of icon (transform-origin: 50% 0)
const bellRing = keyframes`
  0%   { transform: rotate(0deg); }
  5%   { transform: rotate(18deg); }
  15%  { transform: rotate(-15deg); }
  25%  { transform: rotate(12deg); }
  35%  { transform: rotate(-10deg); }
  45%  { transform: rotate(7deg); }
  55%  { transform: rotate(-5deg); }
  65%  { transform: rotate(3deg); }
  75%  { transform: rotate(-2deg); }
  85%  { transform: rotate(1deg); }
  100% { transform: rotate(0deg); }
`;

export interface OpportunityNotification {
  id: string;
  type: 'opportunity';
  title: string;
  description: string;
  startDate: Date;
  endDate: Date;
  lifeArea: string;
  severity: number;
}

export interface CautionNotification {
  id: string;
  type: 'caution';
  title: string;
  description: string;
  startDate: Date;
  endDate: Date;
  lifeArea: string;
  severity: number;
  /** Body organs / life systems to watch — populated for health/self area cautions */
  organWatch?: string;
}

export interface YogaNotification {
  id: string;
  type: 'yoga';
  name: string;
  description: string;
  startDate: Date;
  endDate: Date;
  isPermanent: boolean;
}

export interface PredictionNotification {
  id: string;
  type: 'prediction';
  period: 'today' | 'week' | 'month';
  title: string;
  summary: string;
  highlights: string[];
  /** Score delta vs previous window (positive = improving, negative = worsening) */
  scoreDelta?: number;
}

export type Notification = OpportunityNotification | CautionNotification | YogaNotification | PredictionNotification;

interface NotificationCenterProps {
  opportunities: OpportunityNotification[];
  cautions: CautionNotification[];
  yogas: YogaNotification[];
  predictions: PredictionNotification[];
  onNavigate: (section: 'current-period' | 'yogas', itemId?: string) => void;
  birthDate?: Date; // For age-based ordering
  feedStats?: { total: number; positive: number; negative: number; neutral: number };
  subscriptionPlan?: string | null;
  subscriptionDaysRemaining?: number | null;
  onOpenSubscription?: () => void;
}

export default function NotificationCenter({
  opportunities,
  cautions,
  yogas,
  predictions,
  onNavigate,
  birthDate,
  feedStats,
  subscriptionPlan,
  subscriptionDaysRemaining,
  onOpenSubscription,
}: NotificationCenterProps) {
  const [isOpen, setIsOpen] = useState(false);
  const panelRef = useRef<HTMLDivElement>(null);

  // Calculate age from birthDate
  const age = birthDate ? Math.floor((new Date().getTime() - birthDate.getTime()) / (365.25 * 24 * 60 * 60 * 1000)) : null;

  // Close panel when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (panelRef.current && !panelRef.current.contains(event.target as Node)) {
        setIsOpen(false);
      }
    };

    if (isOpen) {
      document.addEventListener('mousedown', handleClickOutside);
      return () => document.removeEventListener('mousedown', handleClickOutside);
    }
  }, [isOpen]);

  // Calculate total notifications count
  const totalCount = opportunities.length + cautions.length + yogas.length + predictions.length;
  const hasNotifications = totalCount > 0;

  // Categorize opportunities and cautions by life area type
  const categorizeByArea = (items: (OpportunityNotification | CautionNotification)[]) => {
    return {
      education: items.filter(i => ['education', 'learning', 'knowledge'].some(k => i.lifeArea.toLowerCase().includes(k))),
      career: items.filter(i => ['career', 'job', 'business', 'profession'].some(k => i.lifeArea.toLowerCase().includes(k))),
      wealth: items.filter(i => ['wealth', 'money', 'finance'].some(k => i.lifeArea.toLowerCase().includes(k))),
      health: items.filter(i => ['health', 'wellness'].some(k => i.lifeArea.toLowerCase().includes(k))),
      family: items.filter(i => ['family', 'father', 'mother', 'parent'].some(k => i.lifeArea.toLowerCase().includes(k))),
      marriage: items.filter(i => ['marriage', 'spouse', 'relationship', 'partner'].some(k => i.lifeArea.toLowerCase().includes(k))),
      property: items.filter(i => ['property', 'real estate', 'land', 'home'].some(k => i.lifeArea.toLowerCase().includes(k))),
      others: items.filter(i => {
        const area = i.lifeArea.toLowerCase();
        return !['education', 'learning', 'knowledge', 'career', 'job', 'business', 'profession',
                 'wealth', 'money', 'finance', 'health', 'wellness', 'family', 'father', 'mother', 'parent',
                 'marriage', 'spouse', 'relationship', 'partner', 'property', 'real estate', 'land', 'home'].some(k => area.includes(k));
      })
    };
  };

  const categorizedOpps = categorizeByArea(opportunities);
  const categorizedCautions = categorizeByArea(cautions);

  // Determine section order based on age
  const getSectionOrder = () => {
    if (!age) return ['predictions', 'cautions', 'opportunities', 'yogas']; // Default order

    if (age >= 5 && age <= 18) {
      // Age 5-18: Cautions, education, health, family, predictions, others
      return ['cautions', 'education', 'health', 'family', 'predictions', 'others'];
    } else if (age >= 19 && age <= 35) {
      // Age 19-35: Predictions, cautions, career/wealth, health, education, marriage, family, property, others
      return ['predictions', 'cautions', 'career', 'wealth', 'health', 'education', 'marriage', 'family', 'property', 'others'];
    } else if (age >= 36 && age <= 45) {
      // Age 36-45: Predictions, cautions, career/wealth, health, family, property, marriage, others
      return ['predictions', 'cautions', 'career', 'wealth', 'health', 'family', 'property', 'marriage', 'others'];
    } else {
      // Age 45+: Predictions, cautions, career/wealth, health, family, property, others
      return ['predictions', 'cautions', 'career', 'wealth', 'health', 'family', 'property', 'others'];
    }
  };

  // Convert severity score to confidence percentage
  const getConfidence = (severity: number): number => {
    // Severity is typically 30-100, convert to percentage
    return Math.min(100, Math.max(0, Math.round(Math.abs(severity))));
  };

  // Sort notifications by confidence/probability (highest first), then by date (nearest first)
  const sortedOpportunities = [...opportunities].sort((a, b) => {
    const confA = getConfidence(a.severity);
    const confB = getConfidence(b.severity);
    if (confA !== confB) return confB - confA; // Higher confidence first
    return a.startDate.getTime() - b.startDate.getTime(); // Then by nearest date
  });
  
  const sortedCautions = [...cautions].sort((a, b) => {
    const confA = getConfidence(a.severity);
    const confB = getConfidence(b.severity);
    if (confA !== confB) return confB - confA; // Higher confidence first
    return a.startDate.getTime() - b.startDate.getTime(); // Then by nearest date
  });
  
  // Sort yogas: time-based yogas first (more urgent), then permanent yogas
  const sortedYogas = [...yogas].sort((a, b) => {
    // Time-based yogas come before permanent yogas
    if (a.isPermanent && !b.isPermanent) return 1;
    if (!a.isPermanent && b.isPermanent) return -1;
    // Within same type, sort by start date
    return a.startDate.getTime() - b.startDate.getTime();
  });

  // Derive tone from a single highlight string
  const getHighlightTone = (h: string): 'success' | 'warning' | 'danger' | 'neutral' => {
    const l = h.toLowerCase();
    if (l.includes('go ahead') || l.includes('strong') || l.includes('excellent') ||
        l.includes('high-potential') || l.includes('generally positive') ||
        l.includes('favourable') || l.includes('favorable') || l.includes('good energy') ||
        l.includes('positive')) return 'success';
    if (l.includes('caution') || l.includes('hold off') || l.includes('challenging') ||
        l.includes('difficult') || l.includes('avoid') || l.includes('careful') ||
        l.includes('management') || l.includes('mixed signals')) return 'danger';
    if (l.includes('mixed') || l.includes('timing') || l.includes('use good windows') ||
        l.includes('low energy') || l.includes('pick')) return 'warning';
    return 'neutral';
  };

  // Derive overall card tone from all highlights
  const getCardTone = (highlights: string[]): 'success' | 'warning' | 'danger' | 'neutral' => {
    if (!highlights.length) return 'neutral';
    const tones = highlights.map(getHighlightTone);
    const s = tones.filter(t => t === 'success').length;
    const d = tones.filter(t => t === 'danger').length;
    const w = tones.filter(t => t === 'warning').length;
    if (d > s) return 'danger';
    if (s > d + w) return 'success';
    if (w > 0 || s === d) return 'warning';
    return 'neutral';
  };

  // Summary text colour per card tone (used as fallback for lines without prefix)
  const summaryColorMap: Record<string, string> = {
    success: '#1a6b3c',
    warning: '#7a4f00',
    danger:  '#8b1a1a',
    neutral: 'text.secondary',
  };

  // Per-line color from embedded prefix: [g]=good(green), [m]=mixed(amber), [c]=caution(red), [n]=neutral
  const prefixColorMap: Record<string, string> = {
    g: '#1a6b3c',   // deep green
    m: '#7a4f00',   // deep amber
    c: '#8b2500',   // deep orange-red
    n: '#555577',   // muted blue-grey
  };
  const parseSummaryLine = (line: string, tone: string): { text: string; color: string } => {
    const match = line.match(/^\[([gmcn])\](.*)$/);
    if (match) return { text: match[2], color: prefixColorMap[match[1]] ?? summaryColorMap[tone] };
    return { text: line, color: summaryColorMap[tone] };
  };

  // Returns true if a high-score opportunity window closes within 36 hours
  const isClosingSoon = (end: Date, score: number): boolean => {
    const hoursLeft = (end.getTime() - Date.now()) / (1000 * 60 * 60);
    return hoursLeft > 0 && hoursLeft <= 36 && score >= 50;
  };

  // Strip technical planet codes / jargon from feed-generated titles
  const cleanTitle = (raw: string): string => {
    // Remove everything from first ':' or '|' onward
    let t = raw.replace(/[:|].*$/, '').trim();
    // Remove leading ALL_CAPS planet codes like SUN_, MOON_, etc.
    t = t.replace(/^[A-Z_]{3,}\s*/g, '');
    // Remove trailing references like "(House 5)" or "[SD]"
    t = t.replace(/\s*[\[(][^\])\n]{1,20}[\])]$/, '').trim();
    t = t.replace(/\s+/g, ' ').trim();
    return t.length > 58 ? t.slice(0, 55) + '…' : t;
  };

  // Returns true if text contains a specific date reference (already shown in date chip)
  const containsDateRef = (text: string): boolean =>
    /\d{1,2}(th|st|nd|rd)?\s+(?:Jan|Feb|Mar|Apr|May|Jun|Jul|Aug|Sep|Oct|Nov|Dec)/i.test(text) ||
    /\d{4}-\d{2}-\d{2}/.test(text);

  // Returns true if text is a raw snake_case rule key (no spaces, underscores only)
  const isRuleKey = (text: string): boolean =>
    /^[a-z][a-z0-9_]{4,}$/.test(text.trim());

  // Sentences that are technical computation noise, not human-readable effects
  const isTechnicalSentence = (s: string): boolean =>
    /Results?\s+determined\s+by|calculated\s+based\s+on|based\s+on.*position|house\s+position.*between/i.test(s) ||
    /^[A-Z][A-Z0-9 _\-]{4,}[:\-]/.test(s); // ALL_CAPS label header

  // Extract the ALL_CAPS cause header and convert to readable title-case label.
  // e.g. "7TH LORD AFFLICTION DETECTED:" → "7th Lord Affliction"
  //      "MARRIAGE & PARTNERSHIP IMPACT:" → "Marriage & Partnership Impact"
  //      "SADE SATI - PEAK PHASE ⚠️:"     → "Sade Sati - Peak Phase"
  const extractCause = (desc: string): string | null => {
    if (!desc || isRuleKey(desc)) return null;
    // Primary: ALL_CAPS header followed by colon (Stage C shortMessage format)
    const match = desc.match(/^[^\w]*([A-Z0-9][A-Z0-9 _\-/&]{3,}?)\s*[⚠️✨🌟💡🔴🟡🟢]*\s*:/);
    // Fallback: ALL_CAPS header followed by a space then non-uppercase (old message format like "YOGA ACTIVATED between...")
    const fallbackMatch = !match ? desc.match(/^[^\w]*([A-Z][A-Z0-9 \-]{4,}?)\s+(?=\d|between|[a-z])/) : null;
    const rawMatch = match || fallbackMatch;
    if (!rawMatch) return null;
    let cause = rawMatch[1].trim();
    // Suppress generic/useless headers like "EFFECTS", "GUIDANCE", "REMEDIES"
    if (/^(EFFECTS?|GUIDANCE|REMEDIES|TIPS?|NOTE|INFO)$/i.test(cause)) return null;
    // Suppress if it's just the life area name — not a meaningful cause
    if (/^(CAREER|HEALTH|WEALTH|SPOUSE|MOTHER|FATHER|CHILDREN|PROPERTY|TRAVEL|SPIRITUALITY|SELF|LITIGATION)$/i.test(cause)) return null;
    // Convert to title case
    cause = cause.toLowerCase()
      .replace(/\b\w/g, c => c.toUpperCase())
      .replace(/\bAnd\b/g, 'and')
      .replace(/\bOf\b/g, 'of')
      .replace(/\bIn\b/g, 'in');
    // Remove trailing "Detected", "Found", "Alert", "Activated" — redundant
    cause = cause.replace(/\s+(Detected|Found|Alert|Warning|Activated)$/i, '').trim();
    return cause.length > 55 ? cause.slice(0, 52) + '…' : cause;
  };

  // Convert snake_case ruleKey to a human-readable label, used as fallback title
  const ruleKeyToLabel = (ruleKey?: string): string | null => {
    if (!ruleKey) return null;
    return ruleKey
      .replace(/_/g, ' ')
      .replace(/\b\w/g, c => c.toUpperCase())
      .trim();
  };

  // Pick the best impact-carrying bullet from a bullet-list description
  const bestBullet = (bullets: string[]): string | null => {
    const impactWords = /tension|challenge|delay|difficult|conflict|pressure|strain|affect|impact|struggle|caution|problem|harm|risk|loss|damage|favor|opportunit|strength|positive|benefit|gain|support|bless|success|growth|improve|relation|marriage|partner|career|wealth|health/i;
    return bullets.find(b => impactWords.test(b)) ?? (bullets[0] ?? null);
  };

  // Cap a string to maxLen chars, breaking at word boundary
  const capText = (t: string, maxLen = 150): string =>
    t.length > maxLen ? t.slice(0, maxLen - 3).replace(/\s\S+$/, '') + '…' : t;

  // Strip trailing date qualifiers: "between 27th Mar... and 17th Apr..."
  const stripTrailingDate = (s: string): string =>
    s.replace(/\s+between\s+\d{1,2}[^.]*$/i, '')
     .replace(/\s+until\s+\d{1,2}[^.]*$/i, '')
     .replace(/\s+from\s+\d{1,2}[^.]*$/i, '')
     .trim();

  // Extract a human-readable effect sentence from a raw feed description.
  // Pass title + notifType to enable a meaningful contextual fallback.
  const extractEffect = (desc: string, title?: string, notifType?: 'opportunity' | 'caution'): string => {
    if (!desc || isRuleKey(desc)) {
      const area = title?.toLowerCase() ?? 'this area';
      return notifType === 'caution'
        ? `Challenges may arise in ${area} matters during this period.`
        : `A favorable window for ${area} may be present during this period.`;
    }

    // 1. EFFECTS: / EFFECT: label — highest-quality source, scan full desc first
    const effectsMatch = desc.match(/EFFECTS?:\s*([^\n]{15,})/i);
    if (effectsMatch) {
      return capText(effectsMatch[1].trim().replace(/\.$/, '')) + '.';
    }

    // 2. Strip leading emoji line + ALL_CAPS detection header (can be multi-word)
    let cleaned = desc
      .replace(/^[^\w\r\n]*/, '')                                   // leading emoji / symbols
      .replace(/^[A-Z0-9][A-Z0-9 _\-/&]{3,}(?:DETECTED|FOUND|ALERT|WARNING|YOGA|PHASE)?:\s*/g, '') // ALL_CAPS header
      .trim();

    // 3. Bullet-point format — find the most impact-carrying bullet
    if (cleaned.includes('•')) {
      const bullets = cleaned.split('•').map(b => b.trim()).filter(b => b.length > 15 && !isTechnicalSentence(b));
      const best = bestBullet(bullets);
      if (best) {
        const stripped = stripTrailingDate(best.replace(/\.$/, ''));
        return capText(stripped) + '.';
      }
    }

    // 4. Walk sentences — skip technical / date-only / fragment / ALL_CAPS ones
    const sentences = cleaned.split(/(?<=[.!?])\s+|\n/);
    for (const s of sentences) {
      let trimmed = s.trim();
      if (trimmed.length < 18) continue;
      if (/^[a-z]/.test(trimmed)) continue;          // fragment starting mid-sentence
      if (isTechnicalSentence(trimmed)) continue;     // computation noise
      // Strip inline date qualifier and check if what remains is useful
      const stripped = stripTrailingDate(trimmed.replace(/\.$/, ''));
      // Skip ALL_CAPS-only fragments left after date stripping, e.g. "GAJA KESARI YOGA ACTIVATED"
      const isOnlyCapsFragment = stripped.length > 0 && /^[A-Z0-9][A-Z0-9 \-/&+!?,]{4,}$/.test(stripped.trim());
      if (stripped.length >= 18 && !isOnlyCapsFragment) {
        return capText(stripped) + '.';
      }
      if (isOnlyCapsFragment) continue; // Skip label fragment, try next sentence
      if (containsDateRef(trimmed) && trimmed.length < 90) continue; // pure date sentence
      return capText(trimmed.match(/[.!?]$/) ? trimmed : trimmed + '.');
    }

    // 5. Contextual fallback — better than showing garbled text
    const area = title?.toLowerCase() ?? 'this area';
    if (notifType === 'caution') return `Challenges may arise in ${area} matters during this period.`;
    if (notifType === 'opportunity') return `A favorable window for ${area} may be present.`;
    return capText(cleaned) + (cleaned.endsWith('.') ? '' : '…');
  };

  // Extract action hint — suppressed if it duplicates the effect or contains a date range or is too short
  const extractAction = (desc: string): string | null => {
    const guidanceMatch = desc.match(/(?:Guidance|What to do|Tip)[:\s]+([^.\n]{20,120})/i);
    if (guidanceMatch) {
      const action = guidanceMatch[1].trim().replace(/\.$/, '');
      if (containsDateRef(action)) return null;
      return action;
    }
    const actionMatch = desc.match(/(?:You should|Consider|Focus on|Ensure|Take advantage of)[^.\n]{20,100}/i);
    if (actionMatch) {
      const action = actionMatch[0].trim().replace(/\.$/, '');
      if (containsDateRef(action)) return null;
      return action;
    }
    return null;
  };

  const formatDateRange = (start: Date, end: Date): string => {
    const opts: Intl.DateTimeFormatOptions = { month: 'short', day: 'numeric' };
    const from = start.toLocaleDateString('en-US', opts);
    const to = end.toLocaleDateString('en-US', opts);
    return `${from} – ${to}`;
  };

  const handleNotificationClick = (notification: Notification) => {
    if (notification.type === 'opportunity' || notification.type === 'caution') {
      onNavigate('current-period', notification.id);
    } else if (notification.type === 'yoga') {
      onNavigate('yogas', notification.id);
    }
    // Predictions don't navigate
    setIsOpen(false);
  };

  return (
    <Box sx={{ position: 'relative' }} ref={panelRef}>
      {/* Bell Icon Button */}
      <IconButton
        variant="soft"
        color={hasNotifications ? 'primary' : 'neutral'}
        onClick={() => setIsOpen(!isOpen)}
        size="sm"
        sx={{ mr: 0.5 }}
      >
        <Badge 
          badgeContent={totalCount} 
          max={99} 
          color="danger"
          sx={{
            '& .MuiBadge-badge': {
              fontSize: '0.7rem',
              minWidth: '16px',
              minHeight: '16px',
              height: '16px',
              width: '16px',
              padding: 0,
              borderRadius: '50%',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center'
            }
          }}
        >
          {hasNotifications ? (
            <NotificationsActiveIcon
              sx={{
                fontSize: '1.1rem',
                transformOrigin: '50% 0%',
                animation: `${bellRing} 2.4s ease-in-out infinite`,
                animationDelay: '1s',
              }}
            />
          ) : (
            <NotificationsIcon sx={{ fontSize: '1.1rem' }} />
          )}
        </Badge>
      </IconButton>

      {/* Notification Panel */}
      {isOpen && (
        <Box
          sx={{
            position: { xs: 'fixed', sm: 'absolute' },
            top: { xs: 'calc(100px + 8px)', sm: 'calc(100% + 8px)' },
            right: { xs: 8, sm: 0 },
            width: { xs: 'calc(100vw - 16px)', sm: 420 },
            maxHeight: '85vh',
            overflowY: 'auto',
            overflowX: 'hidden',
            zIndex: 10002,
            boxShadow: 'lg',
            bgcolor: 'background.surface',
            borderRadius: 'md',
            border: '1px solid',
            borderColor: 'divider',
            p: 0,
            '&::-webkit-scrollbar': { width: '8px' },
            '&::-webkit-scrollbar-track': { background: '#f1f1f1', borderRadius: '4px' },
            '&::-webkit-scrollbar-thumb': { background: '#888', borderRadius: '4px' },
            '&::-webkit-scrollbar-thumb:hover': { background: '#555' }
          }}
        >
          {/* Header */}
          <Box sx={{ p: 1.5, pb: 1, borderBottom: '1px solid', borderColor: 'divider', position: 'sticky', top: 0, bgcolor: 'background.surface', zIndex: 1 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
              <Typography level="title-md" startDecorator={<NotificationsIcon />} sx={{ fontFamily: '"Cinzel", "Garamond", serif', color: '#7a1a2e' }}>
                Notifications
              </Typography>
              <IconButton
                variant="plain"
                color="neutral"
                size="sm"
                onClick={() => setIsOpen(false)}
                sx={{ minWidth: '32px', minHeight: '32px' }}
              >
                <CloseIcon sx={{ fontSize: '1.2rem' }} />
              </IconButton>
            </Box>
            {hasNotifications && (
              <Typography level="body-xs" sx={{ color: 'text.secondary', mt: 0.5 }}>
                {totalCount} active notification{totalCount > 1 ? 's' : ''}
              </Typography>
            )}
          </Box>

          {/* Subscription expiry chip */}
          {(() => {
            const isTrial = subscriptionPlan === 'free_trial';
            const isPaid = subscriptionPlan === 'silver' || subscriptionPlan === 'gold';
            const days = subscriptionDaysRemaining;
            const showChip = onOpenSubscription && (
              (isTrial && days != null) ||
              (isPaid && days != null && days <= 30)
            );
            if (!showChip) return null;
            const isUrgent = days != null && days <= 7;
            const planLabel = isTrial ? 'Free trial' : subscriptionPlan === 'silver' ? 'Silver plan' : 'Gold plan';
            const bgColor = isUrgent ? 'rgba(196,30,30,0.07)' : 'rgba(180,110,0,0.08)';
            const borderColor = isUrgent ? 'rgba(196,30,30,0.25)' : 'rgba(180,110,0,0.28)';
            const textColor = isUrgent ? '#b01a1a' : '#8a5500';
            return (
              <Box sx={{ px: 1.5, pt: 1.5 }}>
                <Box sx={{
                  display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 1,
                  p: 1.25, borderRadius: 'sm',
                  bgcolor: bgColor, border: '1px solid', borderColor,
                }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, minWidth: 0 }}>
                    <WorkspacePremiumIcon sx={{ fontSize: '1rem', color: textColor, flexShrink: 0 }} />
                    <Typography level="body-xs" sx={{ fontWeight: 700, color: textColor, lineHeight: 1.3 }}>
                      {planLabel} expires in {days} day{days !== 1 ? 's' : ''}
                    </Typography>
                  </Box>
                  <Button
                    size="sm"
                    variant="soft"
                    color={isUrgent ? 'danger' : 'warning'}
                    onClick={() => { onOpenSubscription!(); setIsOpen(false); }}
                    sx={{ fontWeight: 700, fontSize: '0.72rem', py: 0.4, px: 1, flexShrink: 0 }}
                  >
                    Check Plans
                  </Button>
                </Box>
              </Box>
            );
          })()}

          {/* Content */}
          {!hasNotifications ? (
            <Box sx={{ p: 3, textAlign: 'center' }}>
              <NotificationsIcon sx={{ fontSize: 48, color: 'neutral.300', mb: 1 }} />
              <Typography level="body-md" sx={{ color: 'text.secondary' }}>
                No new notifications
              </Typography>
            </Box>
          ) : (
            <Box sx={{ pb: 1 }}>
              {/* Render sections in age-appropriate order */}
              {getSectionOrder().map((sectionType) => {
                // Predictions Section
                if (sectionType === 'predictions' && predictions.length > 0) {
                  return (
                    <Box key="predictions">
                      <Typography
                        level="title-sm"
                        startDecorator={<WbSunnyIcon sx={{ fontSize: '0.9rem', color: '#b8860b' }} />}
                        sx={{ px: 1.5, py: 1, bgcolor: 'rgba(212,175,55,0.08)', color: '#7a1a2e', fontFamily: '"Raleway", sans-serif', fontWeight: 700, fontSize: '0.72rem', letterSpacing: '0.04em', borderBottom: '1px solid rgba(212,175,55,0.2)' }}
                      >
                        ShreemAstro Predictor ({predictions.length})
                      </Typography>
                      <Box sx={{ pt: 0.75 }}>
                        {predictions.map((pred) => {
                        const cardTone = getCardTone(pred.highlights);

                        // Left-border + bg tint palette
                        const borderColors: Record<string, string> = {
                          success: '#2e7d32',
                          warning: '#e65100',
                          danger:  '#c62828',
                          neutral: '#757575',
                        };
                        const bgColors: Record<string, string> = {
                          success: '#f1fdf4',
                          warning: '#fff8e1',
                          danger:  '#fff5f5',
                          neutral: 'transparent',
                        };

                        // Period icon
                        const PeriodIcon = pred.period === 'today' ? TodayIcon
                          : pred.period === 'week' ? DateRangeIcon
                          : CalendarMonthIcon;

                        return (
                          <Box
                            key={pred.id}
                            sx={{
                              mx: 1,
                              mb: 1,
                              borderRadius: '8px',
                              border: `1px solid ${borderColors[cardTone]}33`,
                              bgcolor: bgColors[cardTone],
                              overflow: 'hidden',
                            }}
                          >
                            {/* Period header band */}
                            <Box sx={{
                              display: 'flex',
                              alignItems: 'center',
                              gap: 0.75,
                              px: 1.25,
                              py: 0.6,
                              bgcolor: `${borderColors[cardTone]}18`,
                              borderBottom: `1px solid ${borderColors[cardTone]}33`,
                            }}>
                              <PeriodIcon sx={{ fontSize: 13, color: borderColors[cardTone] }} />
                              <Typography level="body-xs" fontWeight="bold" sx={{ fontSize: '0.72rem', color: borderColors[cardTone], flex: 1, textTransform: 'uppercase', letterSpacing: '0.05em' }}>
                                {pred.title}
                              </Typography>
                              {pred.period === 'today' && pred.scoreDelta !== undefined && pred.scoreDelta !== 0 && (
                                <Box sx={{ fontSize: '0.7rem', color: pred.scoreDelta > 0 ? '#1b5e20' : '#b71c1c', fontWeight: 600, flexShrink: 0 }}>
                                  {pred.scoreDelta > 0 ? '▲' : '▼'} {Math.abs(pred.scoreDelta)}%
                                </Box>
                              )}
                            </Box>

                            <Box sx={{ px: 1.25, py: 0.75 }}>
                              {/* Summary lines — each with a coloured dot indicator */}
                              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.3, mb: 0.75 }}>
                                {pred.summary.split('\n').map((line, i) => {
                                  const { text, color } = parseSummaryLine(line, cardTone);
                                  const isOrganLine = text.startsWith('⚕️');
                                  if (isOrganLine) {
                                    return (
                                      <Box key={i} sx={{ display: 'flex', alignItems: 'flex-start', gap: 0.5, mt: 0.25, px: 0.75, py: 0.3, bgcolor: 'rgba(180,83,9,0.07)', borderRadius: '4px', border: '1px solid rgba(180,83,9,0.15)' }}>
                                        <Typography level="body-xs" sx={{ color: '#92400e', lineHeight: 1.4, fontSize: '0.73rem', fontWeight: 500 }}>
                                          {text}
                                        </Typography>
                                      </Box>
                                    );
                                  }
                                  return (
                                    <Box key={i} sx={{ display: 'flex', alignItems: 'flex-start', gap: 0.75 }}>
                                      <Box sx={{ width: 5, height: 5, borderRadius: '50%', bgcolor: color, flexShrink: 0, mt: '5px' }} />
                                      <Typography level="body-xs" sx={{ color, lineHeight: 1.4, fontSize: '0.78rem', flex: 1 }}>
                                        {text}
                                      </Typography>
                                    </Box>
                                  );
                                })}
                              </Box>

                              {/* Highlight chips — area action tags */}
                              {pred.highlights.length > 0 && (
                                <>
                                  <Box sx={{ height: '1px', bgcolor: `${borderColors[cardTone]}25`, mb: 0.6 }} />
                                  <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.4 }}>
                                    {pred.highlights.map((highlight, idx) => {
                                      const tone = getHighlightTone(highlight);
                                      const chipBg: Record<string, string> = {
                                        success: '#e8f5e9',
                                        warning: '#fff3e0',
                                        danger:  '#ffebee',
                                        neutral: '#f5f5f5',
                                      };
                                      const chipColor: Record<string, string> = {
                                        success: '#1b5e20',
                                        warning: '#bf360c',
                                        danger:  '#b71c1c',
                                        neutral: '#424242',
                                      };
                                      const chipBorder: Record<string, string> = {
                                        success: '#a5d6a7',
                                        warning: '#ffcc80',
                                        danger:  '#ef9a9a',
                                        neutral: '#e0e0e0',
                                      };
                                      const IconComp = tone === 'success' ? CheckCircleOutlineIcon
                                        : tone === 'danger' ? ErrorOutlineIcon
                                        : tone === 'warning' ? SwapHorizIcon
                                        : null;

                                      return (
                                        <Box
                                          key={idx}
                                          sx={{
                                            display: 'flex',
                                            alignItems: 'center',
                                            gap: 0.5,
                                            px: 0.75,
                                            py: 0.3,
                                            borderRadius: '5px',
                                            bgcolor: chipBg[tone],
                                            border: `1px solid ${chipBorder[tone]}`,
                                          }}
                                        >
                                          {IconComp && (
                                            <IconComp sx={{ fontSize: '0.8rem', color: chipColor[tone], flexShrink: 0 }} />
                                          )}
                                          <Typography
                                            level="body-xs"
                                            sx={{ fontSize: '0.76rem', lineHeight: 1.3, color: chipColor[tone], fontWeight: 600 }}
                                          >
                                            {highlight}
                                          </Typography>
                                        </Box>
                                      );
                                    })}
                                  </Box>
                                </>
                              )}
                            </Box>
                          </Box>
                        );
                      })}
                      </Box>
                    </Box>
                  );
                }

                // All Cautions
                if (sectionType === 'cautions' && sortedCautions.length > 0) {
                  return (
                    <Box key="cautions" sx={{ mb: 1 }}>
                      <Typography level="title-sm" sx={{ px: 1.5, py: 1, bgcolor: 'warning.50', color: 'warning.700', fontFamily: '"Raleway", sans-serif', fontWeight: 700, fontSize: '0.72rem', letterSpacing: '0.04em', borderBottom: '1px solid', borderColor: 'warning.200' }}>
                        ⚠️ Cautions ({sortedCautions.length})
                      </Typography>
                      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0 }}>
                      {sortedCautions.map((caution, ci) => (
                        <Box
                          key={caution.id}
                          onClick={() => handleNotificationClick(caution)}
                          sx={{
                            px: 1.25,
                            py: 0.9,
                            borderBottom: ci < sortedCautions.length - 1 ? '1px solid' : 'none',
                            borderColor: 'divider',
                            borderLeft: '3px solid',
                            borderLeftColor: 'warning.400',
                            cursor: 'pointer',
                            '&:hover': { bgcolor: 'warning.50' },
                            display: 'flex',
                            gap: 0.75,
                            alignItems: 'flex-start',
                          }}
                        >
                          <WarningIcon sx={{ color: 'warning.500', fontSize: 14, mt: '3px', flexShrink: 0 }} />
                          <Box sx={{ flex: 1, minWidth: 0 }}>
                            {/* Title + life-area badge on same row */}
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mb: 0.2, flexWrap: 'wrap' }}>
                              <Typography level="body-xs" fontWeight="bold" sx={{ fontSize: '0.8rem', lineHeight: 1.3, color: 'warning.800', flex: 1, minWidth: 0 }}>
                                {extractCause(caution.description) || cleanTitle(caution.title)}
                              </Typography>
                              <Box sx={{ px: 0.6, py: '1px', borderRadius: '4px', bgcolor: 'warning.100', border: '1px solid', borderColor: 'warning.300', flexShrink: 0 }}>
                                <Typography level="body-xs" sx={{ fontSize: '0.65rem', fontWeight: 700, color: 'warning.700', textTransform: 'capitalize' }}>
                                  {caution.lifeArea === 'self' ? 'Health' : caution.lifeArea}
                                </Typography>
                              </Box>
                            </Box>
                            {/* Effect */}
                            <Typography level="body-xs" sx={{ color: 'text.secondary', lineHeight: 1.4, fontSize: '0.76rem' }}>
                              {extractEffect(caution.description, caution.lifeArea, caution.type)}
                            </Typography>
                            {/* Tip */}
                            {extractAction(caution.description) && (
                              <Typography level="body-xs" sx={{ color: 'warning.700', fontStyle: 'italic', lineHeight: 1.3, mt: 0.3, fontSize: '0.72rem' }}>
                                💡 {extractAction(caution.description)}
                              </Typography>
                            )}
                            {/* Organ watch */}
                            {caution.organWatch && (
                              <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 0.4, mt: 0.4, px: 0.6, py: 0.3, bgcolor: 'rgba(180,83,9,0.07)', borderRadius: '4px', border: '1px solid rgba(180,83,9,0.18)' }}>
                                <Typography level="body-xs" sx={{ color: '#92400e', lineHeight: 1.4, fontSize: '0.72rem', fontWeight: 600 }}>
                                  ⚕️ Watch: {caution.organWatch}
                                </Typography>
                              </Box>
                            )}
                            {/* Meta chips */}
                            <Box sx={{ display: 'flex', gap: 0.4, mt: 0.5, flexWrap: 'wrap', alignItems: 'center' }}>
                              <Chip size="sm" variant="outlined" sx={{ fontSize: '0.68rem', height: '18px', minHeight: '18px', color: 'text.secondary' }}>
                                {formatDateRange(caution.startDate, caution.endDate)}
                              </Chip>
                              <Chip size="sm" variant="soft" color="warning" sx={{ fontSize: '0.68rem', height: '18px', minHeight: '18px' }}>
                                {getConfidence(caution.severity)}%
                              </Chip>
                              {isClosingSoon(caution.endDate, caution.severity) && (
                                <Chip size="sm" variant="solid" color="danger" sx={{ fontSize: '0.68rem', height: '18px', minHeight: '18px', fontWeight: 700 }}>
                                  Ending soon
                                </Chip>
                              )}
                            </Box>
                          </Box>
                          <ChevronRightIcon sx={{ color: 'neutral.300', fontSize: 14, flexShrink: 0, mt: '2px' }} />
                        </Box>
                      ))}
                      </Box>
                    </Box>
                  );
                }

                // Category-specific opportunities
                const renderCategoryOpportunities = (category: keyof ReturnType<typeof categorizeByArea>, title: string, icon: string) => {
                  const items = categorizedOpps[category];
                  if (items.length === 0) return null;
                  return (
                    <Box key={`opp-${category}`} sx={{ mb: 1 }}>
                      <Typography level="title-sm" sx={{ px: 1.5, py: 1, bgcolor: 'success.50', color: 'success.700', fontFamily: '"Raleway", sans-serif', fontWeight: 700, fontSize: '0.72rem', letterSpacing: '0.04em', borderBottom: '1px solid', borderColor: 'success.200' }}>
                        {icon} {title} Opportunities ({items.length})
                      </Typography>
                      {items.map((opp, oi) => (
                        <Box
                          key={opp.id}
                          onClick={() => handleNotificationClick(opp)}
                          sx={{
                            px: 1.25,
                            py: 0.9,
                            borderBottom: oi < items.length - 1 ? '1px solid' : 'none',
                            borderColor: 'divider',
                            borderLeft: '3px solid',
                            borderLeftColor: 'success.400',
                            cursor: 'pointer',
                            '&:hover': { bgcolor: 'success.50' },
                            display: 'flex',
                            gap: 0.75,
                            alignItems: 'flex-start',
                          }}
                        >
                          <TrendingUpIcon sx={{ color: 'success.500', fontSize: 14, mt: '3px', flexShrink: 0 }} />
                          <Box sx={{ flex: 1, minWidth: 0 }}>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mb: 0.2, flexWrap: 'wrap' }}>
                              <Typography level="body-xs" fontWeight="bold" sx={{ fontSize: '0.8rem', lineHeight: 1.3, color: 'success.800', flex: 1, minWidth: 0 }}>
                                {extractCause(opp.description) || cleanTitle(opp.title)}
                              </Typography>
                              <Box sx={{ px: 0.6, py: '1px', borderRadius: '4px', bgcolor: 'success.100', border: '1px solid', borderColor: 'success.300', flexShrink: 0 }}>
                                <Typography level="body-xs" sx={{ fontSize: '0.65rem', fontWeight: 700, color: 'success.700', textTransform: 'capitalize' }}>
                                  {opp.lifeArea}
                                </Typography>
                              </Box>
                            </Box>
                            <Typography level="body-xs" sx={{ color: 'text.secondary', lineHeight: 1.4, fontSize: '0.76rem' }}>
                              {extractEffect(opp.description, opp.lifeArea, opp.type)}
                            </Typography>
                            {extractAction(opp.description) && (
                              <Typography level="body-xs" sx={{ color: 'success.700', fontStyle: 'italic', lineHeight: 1.3, mt: 0.3, fontSize: '0.72rem' }}>
                                💡 {extractAction(opp.description)}
                              </Typography>
                            )}
                            <Box sx={{ display: 'flex', gap: 0.4, mt: 0.5, flexWrap: 'wrap', alignItems: 'center' }}>
                              <Chip size="sm" variant="outlined" sx={{ fontSize: '0.68rem', height: '18px', minHeight: '18px', color: 'text.secondary' }}>
                                {formatDateRange(opp.startDate, opp.endDate)}
                              </Chip>
                              <Chip size="sm" variant="soft" color="success" sx={{ fontSize: '0.68rem', height: '18px', minHeight: '18px' }}>
                                {getConfidence(opp.severity)}%
                              </Chip>
                              {isClosingSoon(opp.endDate, opp.severity) && (
                                <Chip size="sm" variant="solid" color="danger" sx={{ fontSize: '0.68rem', height: '18px', minHeight: '18px', fontWeight: 700, animation: 'pulse 1.5s ease-in-out infinite', '@keyframes pulse': { '0%,100%': { opacity: 1 }, '50%': { opacity: 0.65 } } }}>
                                  Act now
                                </Chip>
                              )}
                            </Box>
                          </Box>
                          <ChevronRightIcon sx={{ color: 'neutral.300', fontSize: 14, flexShrink: 0, mt: '2px' }} />
                        </Box>
                      ))}
                    </Box>
                  );
                };

                // Render category-specific sections
                if (sectionType === 'education') return renderCategoryOpportunities('education', 'Education', '📚');
                if (sectionType === 'career') return renderCategoryOpportunities('career', 'Career/Business', '💼');
                if (sectionType === 'wealth') return renderCategoryOpportunities('wealth', 'Wealth', '💰');
                if (sectionType === 'health') return renderCategoryOpportunities('health', 'Health', '❤️');
                if (sectionType === 'family') return renderCategoryOpportunities('family', 'Family', '👨‍👩‍👧');
                if (sectionType === 'marriage') return renderCategoryOpportunities('marriage', 'Marriage', '💑');
                if (sectionType === 'property') return renderCategoryOpportunities('property', 'Property', '🏠');
                if (sectionType === 'others') return renderCategoryOpportunities('others', 'Other', '🌟');

                // Yogas
                if (sectionType === 'yogas' && sortedYogas.length > 0) {
                  // Separate time-based and permanent yogas for better organization
                  const timeBasedYogas = sortedYogas.filter(y => !y.isPermanent);
                  const permanentYogas = sortedYogas.filter(y => y.isPermanent);
                  
                  return (
                    <Box key="yogas" sx={{ mb: 1 }}>
                      <Typography level="title-sm" sx={{ px: 1.5, py: 1, bgcolor: 'primary.50', color: 'primary.700', fontFamily: '"Raleway", sans-serif', fontWeight: 700, fontSize: '0.72rem', letterSpacing: '0.04em' }}>
                        ✨ Active Yogas ({timeBasedYogas.length > 0 ? `${timeBasedYogas.length} active` : ''}{timeBasedYogas.length > 0 && permanentYogas.length > 0 ? ', ' : ''}{permanentYogas.length > 0 ? `${permanentYogas.length} permanent` : ''})
                      </Typography>
                      
                      {/* Time-based yogas (more urgent) */}
                      {timeBasedYogas.map((yoga) => (
                        <Box
                          key={yoga.id}
                          onClick={() => handleNotificationClick(yoga)}
                          sx={{
                            px: 1.5,
                            py: 1,
                            borderBottom: '1px solid',
                            borderColor: 'divider',
                            cursor: 'pointer',
                            '&:hover': { bgcolor: 'primary.50' },
                            display: 'flex',
                            gap: 1,
                            alignItems: 'flex-start'
                          }}
                        >
                          <AutoAwesomeIcon sx={{ color: 'primary.600', fontSize: 16, mt: 0.25, flexShrink: 0 }} />
                          <Box sx={{ flex: 1, minWidth: 0 }}>
                            <Typography level="body-sm" fontWeight="bold" sx={{ fontSize: '0.8rem', lineHeight: 1.3 }}>
                              {yoga.name}
                            </Typography>
                            <Typography level="body-xs" sx={{ color: 'text.secondary', lineHeight: 1.4, mt: 0.25 }}>
                              {extractEffect(yoga.description)}
                            </Typography>
                            <Box sx={{ display: 'flex', gap: 0.5, mt: 0.5 }}>
                              <Chip size="sm" variant="solid" color="primary" sx={{ fontSize: '0.65rem', height: '18px', minHeight: '18px' }}>
                                {formatDateRange(yoga.startDate, yoga.endDate)}
                              </Chip>
                            </Box>
                          </Box>
                          <ChevronRightIcon sx={{ color: 'neutral.400', fontSize: 16, flexShrink: 0 }} />
                        </Box>
                      ))}
                      
                      {/* Separator between time-based and permanent yogas */}
                      {timeBasedYogas.length > 0 && permanentYogas.length > 0 && (
                        <Box sx={{ px: 1.5, py: 0.5, bgcolor: 'neutral.50' }}>
                          <Typography level="body-xs" sx={{ color: 'neutral.600', fontStyle: 'italic', fontSize: '0.7rem' }}>
                            Permanent Birth Chart Yogas
                          </Typography>
                        </Box>
                      )}
                      
                      {/* Permanent yogas */}
                      {permanentYogas.map((yoga) => (
                        <Box
                          key={yoga.id}
                          onClick={() => handleNotificationClick(yoga)}
                          sx={{
                            px: 1.5,
                            py: 1,
                            borderBottom: '1px solid',
                            borderColor: 'divider',
                            cursor: 'pointer',
                            '&:hover': { bgcolor: 'primary.50' },
                            display: 'flex',
                            gap: 1,
                            alignItems: 'flex-start',
                            opacity: 0.85 // Slightly muted to indicate permanence
                          }}
                        >
                          <AutoAwesomeIcon sx={{ color: 'primary.500', fontSize: 16, mt: 0.25, flexShrink: 0 }} />
                          <Box sx={{ flex: 1, minWidth: 0 }}>
                            <Typography level="body-sm" fontWeight="bold" sx={{ fontSize: '0.8rem', lineHeight: 1.3 }}>
                              {yoga.name}
                            </Typography>
                            <Typography level="body-xs" sx={{ color: 'text.secondary', lineHeight: 1.4, mt: 0.25 }}>
                              {extractEffect(yoga.description)}
                            </Typography>
                            <Box sx={{ display: 'flex', gap: 0.5, mt: 0.5 }}>
                              <Chip size="sm" variant="soft" color="neutral" sx={{ fontSize: '0.65rem', height: '18px', minHeight: '18px' }}>
                                ♾️ Permanent
                              </Chip>
                            </Box>
                          </Box>
                          <ChevronRightIcon sx={{ color: 'neutral.400', fontSize: 16, flexShrink: 0 }} />
                        </Box>
                      ))}
                    </Box>
                  );
                }

                return null;
              })}

            </Box>
          )}
        </Box>
      )}
    </Box>
  );
}
